import { createMemo, JSX, splitProps } from 'solid-js';

export interface NineSliceFrameProps {
  /** Path to the image (relative to public folder) */
  imagePath: string;
  /** Slice values - number for uniform, object for per-edge */
  slice?: number | { top: number; right: number; bottom: number; left: number };
  /** Visual border width in pixels */
  borderWidth?: number;
  /** Border repeat mode */
  repeat?: 'stretch' | 'repeat' | 'round' | 'space';
  /** Use 'fill' to show center of image as background */
  fill?: boolean;
  /** Use pixelated rendering for pixel art */
  pixelated?: boolean;
  /** Child content */
  children?: JSX.Element;
  /** Additional CSS class names */
  class?: string;
  /** Additional inline styles */
  style?: JSX.CSSProperties | string;
}

export const NineSliceFrame = (props: NineSliceFrameProps) => {
  const [local, others] = splitProps(props, [
    'imagePath',
    'slice',
    'borderWidth',
    'repeat',
    'fill',
    'pixelated',
    'children',
    'class',
    'style',
  ]);

  const slice = () => local.slice ?? 8;
  const borderWidth = () => local.borderWidth ?? 5;
  const repeat = () => local.repeat ?? 'stretch';
  const fill = () => local.fill ?? true;
  const pixelated = () => local.pixelated ?? true;

  const sliceValue = createMemo(() => {
    const s = slice();
    return typeof s === 'number' ? s.toString() : `${s.top} ${s.right} ${s.bottom} ${s.left}`;
  });

  const borderImageValue = createMemo(() => {
    return `url("${local.imagePath}") ${sliceValue()} ${fill() ? 'fill' : ''} / ${borderWidth()}px / 0px ${repeat()}`;
  });

  const frameStyle = createMemo((): JSX.CSSProperties => {
    const baseStyle: JSX.CSSProperties = {
      'border-width': '1px',
      'border-style': 'solid',
      'border-image': borderImageValue(),
      'image-rendering': pixelated() ? 'pixelated' : 'auto',
    };

    if (typeof local.style === 'object' && local.style !== null) {
      return { ...baseStyle, ...local.style };
    }

    return baseStyle;
  });

  return (
    <div class={local.class} style={frameStyle()} {...others}>
      {local.children}
    </div>
  );
};
